import {
  adminExerciseDetailSchema,
  adminExerciseListItemSchema,
  type AdminExerciseCreateRequest,
  type AdminExerciseDetail,
  type AdminExerciseEdgeRequest,
  type AdminExerciseListItem,
  type AdminExerciseUpdateRequest,
} from '@steado/admin-contract/exercises';
import { paginationMetaSchema, type PaginationMeta } from '@steado/admin-contract/pagination';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

export interface ExercisesFilters {
  readonly search?: string | undefined;
  readonly category?: string | undefined;
  readonly difficulty?: string | undefined;
  readonly equipment?: string | undefined;
  readonly muscle?: string | undefined;
  readonly activity?: string | undefined;
  readonly source?: string | undefined;
  readonly media?: string | undefined;
  readonly cursor?: string | undefined;
}

export interface ExercisesPage {
  readonly items: AdminExerciseListItem[];
  readonly pagination: PaginationMeta;
}

const exercisesPageSchema = z.object({
  items: z.array(adminExerciseListItemSchema),
  pagination: paginationMetaSchema,
});

export const EXERCISES_PAGE_SIZE = 20;

export function exercisesQueryOptions(api: ApiClient, filters: ExercisesFilters) {
  const query: Record<string, QueryValue> = {
    search: filters.search,
    category: filters.category,
    difficulty: filters.difficulty,
    equipment: filters.equipment,
    muscle: filters.muscle,
    activity: filters.activity,
    source: filters.source,
    media: filters.media,
    cursor: filters.cursor,
    limit: EXERCISES_PAGE_SIZE,
  };

  return queryOptions({
    queryKey: ['admin', 'exercises', filters],
    queryFn: async (): Promise<ExercisesPage> => {
      const response = await api.requestWithMeta({
        path: '/admin/exercises',
        query,
        schema: z.array(adminExerciseListItemSchema),
      });

      return exercisesPageSchema.parse({
        items: response.data,
        pagination: response.meta.pagination,
      });
    },
    placeholderData: (previous) => previous,
  });
}

export function exerciseQueryOptions(api: ApiClient, exerciseId: string | null) {
  return queryOptions({
    queryKey: ['admin', 'exercise', exerciseId],
    queryFn: (): Promise<AdminExerciseDetail> =>
      api.request({ path: `/admin/exercises/${exerciseId ?? ''}`, schema: adminExerciseDetailSchema }),
    enabled: exerciseId !== null,
  });
}

export function createExercise(
  api: ApiClient,
  input: AdminExerciseCreateRequest,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: '/admin/exercises',
    method: 'POST',
    body: input,
    schema: adminExerciseDetailSchema,
  });
}

export function updateExercise(
  api: ApiClient,
  exerciseId: string,
  patch: AdminExerciseUpdateRequest,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}`,
    method: 'PATCH',
    body: patch,
    schema: adminExerciseDetailSchema,
  });
}

/**
 * Deletes an exercise nothing references.
 *
 * A 204, so there is nothing to parse — `z.null()` is what the client expects
 * for an empty body. A row with training history behind it answers 409 instead,
 * and the message names the counts.
 */
export async function deleteExercise(api: ApiClient, exerciseId: string): Promise<void> {
  await api.request({
    path: `/admin/exercises/${exerciseId}`,
    method: 'DELETE',
    schema: z.null(),
  });
}

/** One still, appended. Multipart for the same reason the clip upload is. */
export function uploadExerciseImage(
  api: ApiClient,
  exerciseId: string,
  file: File,
): Promise<AdminExerciseDetail> {
  const form = new FormData();
  form.append('file', file, file.name);

  return api.request({
    path: `/admin/exercises/${exerciseId}/images`,
    method: 'POST',
    body: form,
    schema: adminExerciseDetailSchema,
  });
}

export function removeExerciseImage(
  api: ApiClient,
  exerciseId: string,
  key: string,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/images`,
    method: 'DELETE',
    // The key travels in the body because object keys contain slashes.
    body: { key },
    schema: adminExerciseDetailSchema,
  });
}

export function deactivateExercise(
  api: ApiClient,
  exerciseId: string,
  reason: string,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/deactivate`,
    method: 'POST',
    body: { reason },
    schema: adminExerciseDetailSchema,
  });
}

export function reactivateExercise(api: ApiClient, exerciseId: string): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/reactivate`,
    method: 'POST',
    schema: adminExerciseDetailSchema,
  });
}

/**
 * The one multipart request in the console. `FormData` goes to the client
 * untouched — see `client.ts` for why it must not carry a content-type header.
 */
export function uploadExerciseMedia(
  api: ApiClient,
  exerciseId: string,
  file: File,
): Promise<AdminExerciseDetail> {
  const form = new FormData();
  form.append('file', file, file.name);

  return api.request({
    path: `/admin/exercises/${exerciseId}/media`,
    method: 'POST',
    body: form,
    schema: adminExerciseDetailSchema,
  });
}

export function removeExerciseMedia(api: ApiClient, exerciseId: string): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/media`,
    method: 'DELETE',
    schema: adminExerciseDetailSchema,
  });
}

/** A curated alternative from this exercise to another; returns the exercise with it. */
export function addExerciseEdge(
  api: ApiClient,
  exerciseId: string,
  body: AdminExerciseEdgeRequest,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/edges`,
    method: 'POST',
    body,
    schema: adminExerciseDetailSchema,
  });
}

export function removeExerciseEdge(
  api: ApiClient,
  exerciseId: string,
  edgeId: string,
): Promise<AdminExerciseDetail> {
  return api.request({
    path: `/admin/exercises/${exerciseId}/edges/${edgeId}`,
    method: 'DELETE',
    schema: adminExerciseDetailSchema,
  });
}
